<script setup lang="ts">
import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import { useCounterStore } from "@/stores/counter";
import { useLogin } from "@/stores/login";
import { useMemberService } from "@/stores/memberFunction/memberService";

import VipLevel from "@/components/menberFunction/VIPPrivilege/VipLevel.vue";
import HeaderPhotoNavbar from "@/components/header/headerPhotoNavbar.vue";

import VIPFlagImage1 from "@image/memberFunction/common/VIP1.png";
import VIPFlagImage2 from "@image/memberFunction/common/VIP2.png";
import VIPFlagImage3 from "@image/memberFunction/common/VIP3.png";
import VIPFlagImage4 from "@image/memberFunction/common/VIP4.png";
import VIPFlagImage5 from "@image/memberFunction/common/VIP5.png";
import VIPFlagImage6 from "@image/memberFunction/common/vip6.png";
import VIPFlagImage7 from "@image/memberFunction/common/vip7.png";
import VIPFlagImage8 from "@image/memberFunction/common/vip8.png";
import VIPFlagImage9 from "@image/memberFunction/common/vip9.png";

import image1 from "@image/memberFunction/05/my_app_deposit.png";
import image2 from "@image/memberFunction/05/my_app_pick.png";
import my_top_id_icon from "@image/memberFunction/05/my_top_id_icon.png";
import 認證藍勾 from "@image/memberFunction/06/認證藍勾.png";
import my_top_overage_icon from "@image/memberFunction/05/my_top_overage_icon.png";

import member_pic_01 from "@image/memberFunction/main/member_pic_01.png";

const router = useRouter();
const { t } = useI18n();

const login = useLogin();
const store = useCounterStore();
const memberService = useMemberService();
const { isHeaderPhotoNavbar } = storeToRefs(store);
const { memberData, isZeroVipLevel, isCashMember } = storeToRefs(login);
const { MailCount } = storeToRefs(memberService);
const mode = import.meta.env.VITE_CONFIG_MODE;
const showButtons = ref(mode === 'SF' || mode === 'SFtest' ? false : true);
// 是否顯示 U儲／U託售 入口（由各站 .env 的 VITE_CONFIG_SHOW_USDT 控制）
const showUSDT = import.meta.env.VITE_CONFIG_SHOW_USDT === "true";
// 採用 U儲／U託售 入口的站台（與手機版一致：目前 RM、MT，要套其他站再加進清單）
const usdtLobby = showUSDT && ["RM", "MT"].includes(mode);

const VipData = computed(() => {
  if (JSON.stringify(memberData.value) === "{}") return { nVipLv: false };
  return memberData.value.aVipData;
});

function toPath(path: string) {
  router.push({ name: path });
}

interface HeaderInfoLists {
  title: string;
  path: string;
}

// SF站特殊處理不顯示存款和取款按鈕
const headerInfoLists = computed<HeaderInfoLists[]>(() => {
  if (!showButtons.value) return [];
  const cashBtns: HeaderInfoLists[] = [
    {
      title: t("Header.Deposit"),
      path: "DepositPage",
    },
    {
      title: t("Header.Withdraw"),
      path: "WithdrawPage",
    },
  ];
  // RM/MT：在現金功能列前面加上 U儲／U託售
  return usdtLobby
    ? [
        { title: t("Context.USDT"), path: "USDTDeposit" },
        { title: t("Context.USDTWithdraw"), path: "USDTWithdraw" },
        ...cashBtns,
      ]
    : cashBtns;
});
const imageFile = (path: string) => {
  let getImage;
  switch (path) {
    case "DepositPage":
      getImage = image1;
      break;
    case "WithdrawPage":
      getImage = image2;
      break;
    case "USDTDeposit":
      getImage = `/image/${mode}/memberFunction/05/my_app_usdt.png`;
      break;
    case "USDTWithdraw":
      getImage = `/image/${mode}/memberFunction/05/my_app_usdt-tu.png`;
      break;
    default:
      getImage = "";
      break;
  }
  return getImage;
};

function openLogoutAlert() {
  store.toggleAlertBg();
  store.openLogoutAlert();
}

function toggleHeaderPhotoNavbar() {
  store.toggleHeaderPhotoNavbar();
}
</script>

<template>
  <div class="w-[80vw] md:w-auto">
    <div class="flex justify-between items-center w-full">
      <div class="flex" :class="isCashMember ? 'mt-0' : 'mt-[2dvh]'">
        <button
          type="button"
          class="relative block md:hidden"
          @click="toPath('MemberAccount')">
          <img v-img-src="member_pic_01" alt="" class="w-[40px] mr-[1em]" />
          <span
            class="absolute right-0 -translate-x-1/2 top-0 flex justify-center items-center rounded-full h-[20px] w-[20px] bg-red-500"
            v-if="parseInt(MailCount) > 0">
            <p class="text-primary">{{ MailCount }}</p>
          </span>
        </button>
        <button
          type="button"
          class="relative hidden md:block"
          @click.stop="toggleHeaderPhotoNavbar">
          <img v-img-src="member_pic_01" alt="" class="w-[40px] mr-[1em]" />
          <span
            class="absolute right-0 -translate-x-1/2 top-0 flex justify-center items-center rounded-full h-[20px] w-[20px] bg-red-500"
            v-if="parseInt(MailCount) > 0">
            <p class="text-primary">{{ MailCount }}</p>
          </span>
          <HeaderPhotoNavbar
            class="absolute top-[70px] z-[100]"
            v-if="isHeaderPhotoNavbar" />
        </button>

        <div class="flex flex-col justify-center mr-[1em] text-primary whitespace-nowrap flex-shrink-0">
          <div class="mb-[5px]">
            <img
              v-img-src="my_top_id_icon"
              alt=""
              class="inline w-[14px] mr-[2px] mb-[2px]" />
            <span class="text-primary text-[14px]"
              >{{ $t("Header.Account") }}: {{ memberData.sAccount
              }}</span
            >
            <img
              v-img-src="認證藍勾"
              alt=""
              class="inline w-[14px] ml-[2px] mb-[2px]"
              v-if="memberData.nRank !== '0'" />
          </div>
          <div class="flex items-center mb-[2px]">
            <img
              v-img-src="my_top_overage_icon"
              alt=""
              class="inline w-[14px] mr-[2px]" />
            <span class="text-primary text-[14px]"
              >{{ $t("Header.Balance") }}:</span
            >
            <span class="text-coin text-[18px] ml-1">
              {{ store.getClearPotAndAddCommas(memberData.nMoney).toString() }}
            </span>
          </div>
        </div>
        <ul class="hidden md:flex items-center" v-if="isCashMember">
          <li
            class="mx-[0.6vw]"
            v-for="(item, index) in headerInfoLists"
            :key="item.title + 'headerInfo'">
            <button
              class="flex flex-col items-center justify-between text-primary hover:opacity-50"
              @click="toPath(item.path)"
              v-if="
                login.memberData.nRank == '0' &&
                item.title !== `${$t('Header.Transfer')}`
              ">
              <div class="h-[30px] flex items-center justify-center">
                <img
                  v-img-src="imageFile(item.path)"
                  alt=""
                  class="object-contain"
                  :class="item.path === 'USDTDeposit' ? 'w-[26px] h-[26px]' : item.path === 'USDTWithdraw' ? 'h-[22px] w-auto' : 'w-[30px] h-[30px]'" />
              </div>
              <p class="text-[12px] text-center whitespace-nowrap">{{ item.title }}</p>
            </button>
            <button
              class="flex flex-col items-center justify-between text-primary hover:opacity-50"
              @click="toPath(item.path)"
              v-else-if="login.memberData.nRank !== '0'">
              <div class="h-[30px] flex items-center justify-center">
                <img
                  v-img-src="imageFile(item.path)"
                  alt=""
                  class="object-contain"
                  :class="item.path === 'USDTDeposit' ? 'w-[26px] h-[26px]' : item.path === 'USDTWithdraw' ? 'h-[22px] w-auto' : 'w-[30px] h-[30px]'" />
              </div>
              <p class="text-[12px] text-center whitespace-nowrap">{{ item.title }}</p>
            </button>
          </li>
        </ul>
      </div>
    </div>

    <div class="mt-3 md:hidden" v-if="isCashMember">
      <VipLevel v-if="!isZeroVipLevel" />
    </div>
    <!-- </div> -->
  </div>
</template>
